import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { AnimatePresence, motion } from "framer-motion";
import { ReactComponent as Right } from "../../assets/images/right.svg";
import { ReactComponent as Devices } from "../../assets/images/Devices.svg";
import { ReactComponent as Gallery } from "../../assets/images/Gallery.svg";
import { ReactComponent as SmartPhone } from "../../assets/images/Smartphone.svg";
import { ReactComponent as BankNote } from "../../assets/images/Banknote.svg";

import {
    X,
    CheckCircle2,
    Image as ImageIcon,
    Info,
    BadgeDollarSign,
    CalendarDays,
    ImagePlus,
    Home,
} from "lucide-react";

// ------- small ui helpers -------
function classNames(...xs) {
    return xs.filter(Boolean).join(" ");
}

function formatThaiDateInput(raw) {
    const sanitized = String(raw || "").replace(/[^\d/]/g, "");
    if (!sanitized) return "";

    const slashCount = (sanitized.match(/\//g) || []).length;
    const parts = sanitized.split("/");
    const day = String(parts[0] || "").replace(/\D/g, "").slice(0, 2);
    const month = String(parts[1] || "").replace(/\D/g, "").slice(0, 2);
    const year = String(parts[2] || "").replace(/\D/g, "").slice(0, 4);

    let out = day;
    if (day.length < 2) return out;
    out += "/";
    if (month) out += month;
    if (month && (slashCount >= 2 || month.length >= 2 || year)) out += "/";
    if (year) out += year;
    return out;
}

function formatNowDateTimeInput() {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = String(now.getFullYear());
    const hh = String(now.getHours()).padStart(2, "0");
    const mi = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
}

function SectionCard({ icon: Icon, title, right, children }) {
    return (
        <div className="rounded-md border border-[#00000026] bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 px-5 py-4">
                <div className="flex items-center gap-2">
                    {Icon && (
                        <span className="inline-flexitems-center justify-center">
                            <Icon size={18} className="text-slate-700" />
                        </span>
                    )}
                    <div className="font-semibold text-slate-900">{title}</div>
                </div>
                {right}
            </div>
            <div className="px-5 pb-5">{children}</div>
        </div>
    );
}

function UnderlineInput({
    label,
    name,
    register,
    required,
    errors,
    placeholder,
    type = "text",
    disabled = false,
    inputMode,
    maxLength,
    onlyDigits = false,
    maxValue,
    thaiDateInput = false,
    forceUppercase = false,
    forceAlphanumeric = false,
}) {
    const hasError = !!errors?.[name];
    return (
        <div className="py-3">
            <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
            <input
                type={type}
                disabled={disabled}
                maxLength={maxLength}
                autoCapitalize={forceUppercase || forceAlphanumeric ? "characters" : "off"}
                inputMode={inputMode}
                placeholder={placeholder}
                onInput={(e) => {
                    if (thaiDateInput) {
                        const next = formatThaiDateInput(e.currentTarget.value || "");
                        e.currentTarget.value = next;
                        return;
                    }
                    if (forceUppercase || forceAlphanumeric) {
                        e.currentTarget.value = String(e.currentTarget.value || "").toUpperCase();
                    }
                    if (forceAlphanumeric) {
                        e.currentTarget.value = String(e.currentTarget.value || "").replace(/[^A-Z0-9]/g, "");
                    }
                    if (!onlyDigits) return;
                    const raw = String(e.currentTarget.value || "").replace(/\D/g, "");
                    if (!raw) {
                        e.currentTarget.value = "";
                        return;
                    }
                    const num = Number(raw);
                    e.currentTarget.value =
                        Number.isFinite(maxValue) && Number.isFinite(num)
                            ? String(Math.min(num, Number(maxValue)))
                            : raw;
                }}
                className={classNames(
                    "mt-1 w-full bg-transparent text-sm outline-none placeholder:text-[#1D161680] hover:border-[#047DED]",
                    "border-b",
                    hasError ? "border-red-500" : "border-[#00000026]",
                    (forceUppercase || forceAlphanumeric) && "uppercase",
                    disabled && "bg-slate-100 cursor-not-allowed text-[#1D161680]"
                )}
                {...register(name, {
                    required: required ? "required" : false,
                    validate: (v) => {
                        if (!Number.isFinite(maxValue)) return true;
                        const digits = String(v ?? "").replace(/\D/g, "");
                        if (!digits) return true;
                        return Number(digits) <= Number(maxValue) || `ต้องไม่เกิน ${maxValue}`;
                    },
                    setValueAs: (v) => {
                        if (thaiDateInput) return formatThaiDateInput(v);
                        if (!onlyDigits) {
                            let next = String(v ?? "");
                            if (forceUppercase || forceAlphanumeric) next = next.toUpperCase();
                            if (forceAlphanumeric) next = next.replace(/[^A-Z0-9]/g, "");
                            return next;
                        }
                        const digits = String(v ?? "").replace(/\D/g, "");
                        if (!digits) return "";
                        const num = Number(digits);
                        if (Number.isFinite(maxValue) && Number.isFinite(num)) {
                            return String(Math.min(num, Number(maxValue)));
                        }
                        return digits;
                    },
                })}
            />
        </div>
    );
}

function UnderlineSelect({ label, name, register, required, errors, options = [], placeholder = "เลือกข้อมูล" }) {
    const hasError = !!errors?.[name];
    return (
        <div className="py-3">
            <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
            <select
                defaultValue=""
                className={classNames(
                    "mt-1 w-full bg-transparent text-sm outline-none hover:border-[#047DED]",
                    "border-b text-black",
                    hasError ? "border-red-500" : "border-[#00000026]"
                )}
                {...register(name, { required: required ? "required" : false })}
            >
                <option value="" disabled>{placeholder}</option>
                {options.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
            {hasError && <div className="mt-1 text-xs text-red-600">{errors?.[name]?.message || "required"}</div>}
        </div>
    );
}

function UnderlineTextarea({ label, name, register, required, errors, placeholder, rows = 3 }) {
    const hasError = !!errors?.[name];
    return (
        <div className="py-3">
            <div className="text-sm text-[#1D1616B2] font-semibold">{label}</div>
            <textarea
                rows={rows}
                placeholder={placeholder}
                className={classNames(
                    "mt-1 w-full resize-none bg-transparent text-sm outline-none placeholder:text-[#1D161680]",
                    "border-b",
                    hasError ? "border-red-500" : "border-[#00000026]"
                )}
                {...register(name, { required: required ? "required" : false })}
            />
        </div>
    );
}

function RadioRow({ name, register, required, errors, options }) {
    const hasError = !!errors?.[name];
    return (
        <div className="py-3">
            <div className={classNames("mt-2 space-y-2 border-b pb-3", hasError ? "border-red-500" : "border-[#00000026]")}>
                {options.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 text-sm text-slate-800">
                        <input
                            type="radio"
                            value={opt.value}
                            className="h-4 w-4"
                            {...register(name, { required: required ? "required" : false })}
                        />
                        <span>{opt.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

function SuccessModal({ open, secondsLeft, onClose }) {
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        className="fixed inset-0 z-[100] bg-black/50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />
                    <motion.div
                        className="fixed inset-0 z-[101] flex items-center justify-center p-4"
                        initial={{ opacity: 0, y: 10, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 260, damping: 22 }}
                    >
                        <div className="w-full max-w-sm rounded-md bg-white p-6 shadow-xl">
                            <div className="flex flex-col items-center text-center">
                                <div className="h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center">
                                    <CheckCircle2 size={34} className="text-emerald-600" />
                                </div>
                                <div className="mt-3 text-lg font-extrabold text-slate-900">บันทึกข้อมูลสำเร็จ</div>
                                <div className="mt-1 text-sm text-slate-500">
                                    ระบบจะย้อนกลับใน <span className="font-bold text-slate-900">{secondsLeft}</span> วินาที
                                </div>

                                <button
                                    onClick={onClose}
                                    className="mt-5 w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:opacity-95 active:scale-[0.99]"
                                >
                                    ปิด
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

/* =========================================================
   ✅ ฟอร์มเดียว: ใช้ของเดิมครบ (Photos + Details + Price + Extra)
   mode: "create" | "detail"
========================================================= */
function PricingQueueForm({ mode, imei }) {
    const navigate = useNavigate();
    const hardWarrantyOptions = useMemo(
        () => [
            { value: "ประกันศูนย์ 1 ปี", label: "ประกันศูนย์ 1 ปี" },
            { value: "ประกันศูนย์", label: "ประกันศูนย์" },
            { value: "ประกันหมด", label: "ประกันหมด" },
        ],
        []
    );

    const defaultValues = useMemo(() => {
        if (mode === "create") {
            return {
                received_at: formatNowDateTimeInput(),
                product_name: "",
                model: "",
                color: "",
                capacity: "",
                accessories: "",
                usage: "",
                quality: "",
                battery_max: "",
                cycle_count: "",
                warranty: "",
                warranty_expire: "",
                imei: "",
                admin_desc: "",
                website_desc: "",
                facebook_ref: "",
                price_cost: "",
                price: "",
                price_cash: "",
                price_transfer: "",
                price_other: "",
                installment: "",
                price_mode: "discount",
                note: "",
            };
        }
        // detail (เดิม)
        return {
            received_at: "10/01/2026 09:01:55",
            product_name: "iPhone",
            model: "iPhone 14 Pro",
            color: "ดำ",
            capacity: "128GB",
            accessories: "ครบกล่อง",
            usage: "มือหนึ่ง",
            quality: "สินค้าใหม่มือหนึ่ง",
            battery_max: "100",
            cycle_count: "20",
            warranty: "ประกันศูนย์ 1 ปี",
            warranty_expire: "",
            imei: imei ? decodeURIComponent(imei).toUpperCase() : "",
            admin_desc: "เครื่องใหม่ ยังไม่แกะกล่อง ศูนย์ไทย\n⭐ ดาวน์ 20,500 ผ่อนชำระ ไม่เช็คเครดิต",
            website_desc: "สินค้าเครื่องใหม่ ยังไม่แกะกล่อง ศูนย์ไทย\nอุปกรณ์ครบ",
            facebook_ref: "https://www.facebook.com/share/p/1AeiLbuL3V/",
            price_cost: "69900.00",
            price: "0.00",
            price_cash: "",
            price_transfer: "",
            price_other: "",
            installment: "⭐ ดาวน์ 20,500 ผ่อน 4,650*12 เดือน",
            price_mode: "discount",
            note: "-",
        };
    }, [mode, imei]);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm({ defaultValues, mode: "onSubmit" });

    const selectedWarranty = String(watch("warranty") || "").trim();
    const isWarrantyRemaining = selectedWarranty === "ประกันศูนย์";

    // photos (เดิมครบ)
    const [photos, setPhotos] = useState([]);
    const inputUploadRef = useRef(null);
    const inputCameraRef = useRef(null);
    const MAX_PHOTOS = 50;

    function addFiles(fileList) {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        setPhotos((prev) => {
            const remain = Math.max(0, MAX_PHOTOS - prev.length);
            const picked = files.slice(0, remain).map((f) => ({
                id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                file: f,
                url: URL.createObjectURL(f),
            }));
            return [...prev, ...picked];
        });
    }

    function removePhoto(id) {
        setPhotos((prev) => {
            const found = prev.find((x) => x.id === id);
            if (found?.url) URL.revokeObjectURL(found.url);
            return prev.filter((x) => x.id !== id);
        });
    }

    // success modal
    const [saving, setSaving] = useState(false);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(3);

    const onSubmit = async (values) => {
        setSaving(true);

        setTimeout(() => {
            setSaving(false);
            setOpenSuccess(true);
            setSecondsLeft(3);

            let s = 3;
            const t = setInterval(() => {
                s -= 1;
                setSecondsLeft(s);
                if (s <= 0) {
                    clearInterval(t);
                    setOpenSuccess(false);
                    navigate(-1);
                }
            }, 1000);
        }, 450);

        console.log("submit", { mode, values, photos });
    };

    const closeSuccess = () => {
        setOpenSuccess(false);
        navigate(-1);
    };

    return (
        <div className="space-y-4 bg-[#F5F5F5] p-4 min-h-full">
            {/* header: create ให้เหมือนรูป / detail ใช้ model */}
            <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Home size={16} />
                    <span className="hover:underline cursor-pointer" onClick={() => navigate(-1)}>หน้าหลัก</span>
                    <span className="text-slate-300">/</span>
                    <span className="font-semibold text-[#047DED]">{mode === "create" ? "สร้างออเดอร์" : defaultValues.model}</span>
                </div>

                <div className="flex items-end justify-between gap-3">
                    <div>
                        <div className="text-4xl font-extrabold text-slate-900">
                            {mode === "create" && <Right className="text-4xl inline-block mr-2 text-[#1D1616]" />}
                            {mode === "create" ? "สร้างออเดอร์" : defaultValues.model}
                        </div>

                        <div className="mt-1 flex items-center gap-2 text-slate-500">
                            {mode !== "create" && <CalendarDays size={16} />}
                            <span className="text-sm">
                                {mode === "create"
                                    ? "สร้างออเดอร์สำหรับสินค้าหลัก"
                                    : `สร้างออเดอร์เมื่อ ${defaultValues.received_at}`}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* action bar */}
            <div className="rounded-md border border-slate-50 bg-white px-5 py-3 shadow-md">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-lg font-semibold text-slate-900">บันทึกรายการ</div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="h-10 rounded-md border border-[#00000026] bg-white px-4 text-sm font-semibold text-slate-700 hover:shadow-sm active:scale-[0.98]"
                        >
                            ยกเลิก
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit(onSubmit)}
                            disabled={saving}
                            className={classNames(
                                "h-10 rounded-md px-5 text-sm font-semibold text-white shadow-sm active:scale-[0.98] hover:bg-[#4C4444]",
                                saving ? "bg-slate-400" : "bg-slate-900 hover:opacity-95"
                            )}
                        >
                            {saving ? "กำลังบันทึก..." : mode === "create" ? "บันทึกการซื้อเข้า" : "บันทึก"}
                        </button>
                    </div>
                </div>
            </div>

            {/* ✅ Photos (ของเดิมครบ) */}
            <SectionCard
                icon={Gallery}
                title="รูปภาพสินค้า"
                right={<div className="text-sm text-slate-500">{photos.length}/{MAX_PHOTOS}</div>}
            >
                <div className="min-w-0">
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9">
                        {photos.map((p, idx) => (
                            <div key={p.id} className="group relative">
                                <div className="w-full aspect-square overflow-hidden rounded-md border border-[#00000026] bg-slate-50">
                                    <img src={p.url} alt="photo" className="h-full w-full object-cover" />
                                </div>

                                {idx === 0 && (
                                    <div className="absolute left-1 top-1 rounded-lg bg-[#047DED] px-2 py-0.5 text-[11px] font-bold text-white">
                                        ภาพปก
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={() => removePhoto(p.id)}
                                    className="absolute right-1 top-1 hidden h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white group-hover:inline-flex"
                                    title="ลบ"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={() => inputUploadRef.current?.click()}
                            disabled={photos.length >= MAX_PHOTOS}
                            className={classNames(
                                "w-full aspect-square rounded-md bg-blue-50",
                                "flex flex-col items-center justify-center gap-1 text-[#047DED]",
                                "hover:shadow-sm active:scale-[0.98] transition hover:border-2 hover:border-[#047DED4D]",
                                photos.length >= MAX_PHOTOS && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            <ImagePlus size={22} className="text-[#047DED]" />
                            <span className="text-[11px] font-semibold text-[#047DED]">เพิ่มรูปภาพ</span>
                        </button>
                    </div>

                    <input
                        ref={inputUploadRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => addFiles(e.target.files)}
                    />
                    <input
                        ref={inputCameraRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => addFiles(e.target.files)}
                    />
                </div>
            </SectionCard>

            {/* ✅ Details (ของเดิมครบ) */}
            <SectionCard icon={SmartPhone} title="รายละเอียดสินค้า">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-1">
                    <div>
                        <UnderlineInput label="สินค้าเข้าเมื่อวันที่" name="received_at" register={register} required errors={errors} />
                        <UnderlineInput label="ชื่อสินค้า" name="product_name" register={register} required errors={errors} />
                        <UnderlineInput label="Model" name="model" register={register} required errors={errors} />
                        <UnderlineInput label="สี" name="color" register={register} required errors={errors} />
                        <UnderlineInput label="ความจุ" name="capacity" register={register} required errors={errors} />
                        <UnderlineInput label="อุปกรณ์" name="accessories" register={register} required errors={errors} />
                        <UnderlineInput label="ผ่านการใช้งาน" name="usage" register={register} required errors={errors} />
                        <UnderlineInput label="คุณภาพสินค้า" name="quality" register={register} required errors={errors} />
                        <UnderlineInput
                            label="ความจุแบตเตอรี่สูงสุด"
                            name="battery_max"
                            register={register}
                            required
                            errors={errors}
                            inputMode="numeric"
                            onlyDigits
                            maxValue={100}
                        />
                        <UnderlineInput
                            label="รอบชาร์จ"
                            name="cycle_count"
                            register={register}
                            required
                            errors={errors}
                            inputMode="numeric"
                            onlyDigits
                        />
                    </div>

                    <div>
                        <UnderlineSelect
                            label="ประกัน"
                            name="warranty"
                            register={register}
                            required
                            errors={errors}
                            options={hardWarrantyOptions}
                            placeholder="เลือกข้อมูล"
                        />
                        {isWarrantyRemaining && (
                            <UnderlineInput
                                label="วันหมดอายุประกัน"
                                name="warranty_expire"
                                register={register}
                                required
                                errors={errors}
                                placeholder="วัน/เดือน/ปีพ.ศ."
                                thaiDateInput
                            />
                        )}
                        <UnderlineInput
                            label="IMEI"
                            name="imei"
                            register={register}
                            errors={errors}
                            required={false}
                            maxLength={15}
                            forceUppercase
                            forceAlphanumeric
                        />
                        <UnderlineTextarea
                            label="คำอธิบาย: โดยผู้ดูแล"
                            name="admin_desc"
                            register={register}
                            required
                            errors={errors}
                            rows={5}
                            placeholder="พิมพ์ได้หลายบรรทัด..."
                        />
                        <UnderlineTextarea
                            label="รายละเอียดสินค้าสำหรับเว็บไซต์"
                            name="website_desc"
                            register={register}
                            required
                            errors={errors}
                            rows={4}
                        />
                        <UnderlineInput label="Facebook Reference" name="facebook_ref" register={register} required errors={errors} />
                    </div>
                </div>
            </SectionCard>

            {/* ✅ Price (ของเดิมครบ) */}
            <SectionCard icon={BankNote} title="ราคาสินค้า">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-1">
                    {/* <div className="pt-3">
                        <div className="text-sm text-[#1D1616B2] font-semibold">ราคาทุน</div>
                        <div
                            className={classNames(
                                "mt-1 w-full bg-transparent text-sm text-slate-900 outline-none placeholder:text-[#1D161680] hover:border-[#047DED]",
                                "border-b"
                            )}
                        />
                    </div> */}
                    <div>
                        <UnderlineInput label="ราคาทุน" name="price_cost" register={register} required errors={errors} disabled />
                        <UnderlineInput label="ราคาขาย" name="price" register={register} required errors={errors} />
                        <UnderlineInput label="เงินสด" name="price_cash" register={register} errors={errors} />
                        <UnderlineInput label="เงินโอน" name="price_transfer" register={register} errors={errors} />
                        <UnderlineInput label="อื่นๆ" name="price_other" register={register} errors={errors} />
                        <UnderlineInput label="เรทผ่อน" name="installment" register={register} required errors={errors} />
                    </div>
                    <div>
                        <RadioRow
                            name="price_mode"
                            register={register}
                            required
                            errors={errors}
                            options={[
                                { value: "discount", label: "สินค้ากำลังลดราคา" },
                                { value: "recommend", label: "สินค้านี้เป็นสินค้าแนะนำ" },
                            ]}
                        />
                    </div>
                </div>
            </SectionCard>

            {/* ✅ Extra (ของเดิมครบ) */}
            <SectionCard title="เพิ่มเติม">
                {/* <UnderlineInput label="หมายเหตุ" name="note" register={register} required errors={errors} /> */}
                <UnderlineTextarea
                    label="หมายเหตุ"
                    name="note"
                    register={register}
                    required
                    errors={errors}
                    rows={4}
                />
            </SectionCard>

            <SuccessModal open={openSuccess} secondsLeft={secondsLeft} onClose={closeSuccess} />
        </div>
    );
}

/* =========================================================
   ✅ MAIN EXPORT: ไม่มี params => create UI (แต่ “ของเดิมครบ”)
========================================================= */
export default function PricingQueueDetail() {
    const params = useParams();
    const location = useLocation();
    const { imei } = params || {};

    const pathname = location.pathname || "";
    const imeiLower = String(imei || "").toLowerCase();

    const isCreate = !imei || imeiLower === "create" || pathname.includes("/pricing-queue/create");

    return isCreate ? <PricingQueueForm mode="create" /> : <PricingQueueForm mode="detail" imei={imei} />;
}
